import React, { useState, useEffect, useMemo } from 'react';
import { Layout, PageHeader } from '../components/Layout';
import { useAuth } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Plus, FileText, Receipt, Euro, Download, Send, Check, X, MoreHorizontal, Printer, FileSpreadsheet, Trash2, XCircle, Filter, ClipboardList, Bell, Clock, AlertTriangle, Mail } from 'lucide-react';
import { format, parseISO, startOfMonth, endOfMonth, startOfYear, endOfYear, subYears, isWithinInterval, startOfDay, endOfDay } from 'date-fns';
import { fr } from 'date-fns/locale';
import axios from 'axios';
import { InvoicesTab } from '../components/billing/InvoicesTab';
import { QuotesTab } from '../components/billing/QuotesTab';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const quoteStatusConfig = {
  brouillon: { label: 'Brouillon', color: 'bg-slate-100 text-slate-700' },
  envoye: { label: 'Envoyé', color: 'bg-blue-100 text-blue-800' },
  accepte_attente_bc: { label: 'En attente BC', color: 'bg-amber-100 text-amber-800' },
  accepte: { label: 'Accepté', color: 'bg-emerald-100 text-emerald-800' },
  refuse: { label: 'Refusé', color: 'bg-red-100 text-red-800' },
};

const invoiceStatusConfig = {
  brouillon: { label: 'Brouillon', color: 'bg-slate-100 text-slate-700' },
  envoyee: { label: 'Envoyé', color: 'bg-blue-100 text-blue-800' },
  en_attente: { label: 'En attente', color: 'bg-amber-100 text-amber-800' },
  partiel: { label: 'Partiel', color: 'bg-orange-100 text-orange-800' },
  payee: { label: 'Réglé', color: 'bg-emerald-100 text-emerald-800' },
  trop_percu: { label: 'Trop-perçu', color: 'bg-orange-200 text-orange-900' },
  annule: { label: 'Annulé', color: 'bg-red-100 text-red-800' },
};

export default function BillingPage() {
  const { user } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('invoices');
  
  // Quote dialog
  const [quoteDialogOpen, setQuoteDialogOpen] = useState(false);
  const [quoteFormData, setQuoteFormData] = useState({
    reservation_id: '',
    amount: '',
    description: ''
  });

  // Invoice dialog
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [invoiceFormData, setInvoiceFormData] = useState({
    reservation_id: '',
    amount: '',
    description: ''
  });

  // Payment dialog
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [paymentFormData, setPaymentFormData] = useState({
    amount: '',
    method: 'virement',
    reference: ''
  });
  // Overpayment choice dialog (Don vs Trop-perçu) when amount > remaining
  const [overpayDialogOpen, setOverpayDialogOpen] = useState(false);
  const [pendingPayment, setPendingPayment] = useState(null);

  // Regularization dialog (Trop-perçu refund vs Erreur de saisie)
  const [regularizationDialogOpen, setRegularizationDialogOpen] = useState(false);
  const [regularizationFormData, setRegularizationFormData] = useState({
    amount: '',
    method: 'virement',
    reference: '',
    payment_type: 'regularisation_trop_percu',
  });

  // Export dialog
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportPeriod, setExportPeriod] = useState('current_month');
  const [exportStartDate, setExportStartDate] = useState('');
  const [exportEndDate, setExportEndDate] = useState('');

  // Delete/Cancel dialogs
  const [deleteQuoteDialog, setDeleteQuoteDialog] = useState({ open: false, quote: null });
  const [cancelQuoteDialog, setCancelQuoteDialog] = useState({ open: false, quote: null });
  const [deleteInvoiceDialog, setDeleteInvoiceDialog] = useState({ open: false, invoice: null });
  const [cancelInvoiceDialog, setCancelInvoiceDialog] = useState({ open: false, invoice: null });

  // Quote acceptance with purchase order
  const [acceptQuoteDialog, setAcceptQuoteDialog] = useState({ open: false, quote: null });
  const [purchaseOrder, setPurchaseOrder] = useState('');

  // Edit quote dialog
  const [editQuoteDialog, setEditQuoteDialog] = useState({ open: false, quote: null });
  const [editQuoteFormData, setEditQuoteFormData] = useState({ amount: '', description: '' });

  // Purchase Order validation dialog (for quotes in accepte_attente_bc status)
  const [poValidationDialog, setPoValidationDialog] = useState({ open: false, quote: null });
  const [poNumber, setPoNumber] = useState('');
  const [poValidating, setPoValidating] = useState(false);

  // Reminders dialog
  const [remindersDialog, setRemindersDialog] = useState({ open: false, invoice: null });
  const [invoiceReminders, setInvoiceReminders] = useState([]);
  const [loadingReminders, setLoadingReminders] = useState(false);
  const [sendingReminder, setSendingReminder] = useState(false);

  // Filters for invoices
  const [invoiceStatusFilter, setInvoiceStatusFilter] = useState('all');
  const [invoiceStartDate, setInvoiceStartDate] = useState('');
  const [invoiceEndDate, setInvoiceEndDate] = useState('');

  // Filters for quotes
  const [quoteStatusFilter, setQuoteStatusFilter] = useState('all');
  const [quoteStartDate, setQuoteStartDate] = useState('');
  const [quoteEndDate, setQuoteEndDate] = useState('');

  // Pagination state
  const [invoicePageSize, setInvoicePageSize] = useState(30);
  const [invoiceCurrentPage, setInvoiceCurrentPage] = useState(1);
  const [quotePageSize, setQuotePageSize] = useState(30);
  const [quoteCurrentPage, setQuoteCurrentPage] = useState(1);

  const isAdmin = user?.role === 'admin';
  const isGestionnaire = user?.role === 'gestionnaire';
  const isUser = user?.role === 'utilisateur';

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [quotesRes, invoicesRes, resRes] = await Promise.all([
        api.get('/quotes'),
        api.get('/invoices'),
        api.get('/reservations')
      ]);
      setQuotes(quotesRes.data);
      setInvoices(invoicesRes.data);
      // Filter reservations for quote/invoice creation
      setReservations(resRes.data.filter(r => 
        r.status === 'confirme' || r.status === 'termine' || r.status === 'en_attente'
      ));
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateQuote = async (e) => {
    e.preventDefault();
    try {
      await api.post('/quotes', {
        ...quoteFormData,
        amount: parseFloat(quoteFormData.amount)
      });
      toast.success('Devis créé');
      setQuoteDialogOpen(false);
      setQuoteFormData({ reservation_id: '', amount: '', description: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const handleCreateInvoice = async (e) => {
    e.preventDefault();
    try {
      await api.post('/invoices', {
        ...invoiceFormData,
        amount: parseFloat(invoiceFormData.amount)
      });
      toast.success('Appel à participation créé');
      setInvoiceDialogOpen(false);
      setInvoiceFormData({ reservation_id: '', amount: '', description: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const handleCreatePayment = async (e) => {
    e.preventDefault();
    const amount = parseFloat(paymentFormData.amount);
    const invoiceAmount = selectedInvoice ? Number(selectedInvoice.amount) : 0;
    // Allow 0€ payment only when the invoice itself is 0€ (convention case).
    if (isNaN(amount) || amount < 0) {
      toast.error('Le montant doit être positif ou nul');
      return;
    }
    if (amount === 0 && invoiceAmount > 0.01) {
      toast.error('Le montant doit être positif');
      return;
    }
    const amountDue = selectedInvoice
      ? Number(selectedInvoice.amount) - Number(selectedInvoice.amount_paid || 0)
      : 0;
    // If user types more than what's owed, ask Don vs Trop-perçu.
    if (selectedInvoice && amount > amountDue + 0.01 && amountDue > 0) {
      setPendingPayment({
        invoice_id: selectedInvoice.id,
        amount,
        method: paymentFormData.method,
        reference: paymentFormData.reference,
        amount_due: amountDue,
        excess: Math.round((amount - amountDue) * 100) / 100,
      });
      setOverpayDialogOpen(true);
      return;
    }
    await submitPayment({
      invoice_id: selectedInvoice.id,
      amount,
      method: paymentFormData.method,
      reference: paymentFormData.reference,
      payment_type: 'regulier',
    });
  };

  const submitPayment = async (payload) => {
    try {
      await api.post('/payments', payload);
      toast.success('Règlement enregistré');
      setPaymentDialogOpen(false);
      setOverpayDialogOpen(false);
      setRegularizationDialogOpen(false);
      setPendingPayment(null);
      setPaymentFormData({ amount: '', method: 'virement', reference: '' });
      setRegularizationFormData({ amount: '', method: 'virement', reference: '', payment_type: 'regularisation_trop_percu' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    }
  };

  const handleConfirmOverpayChoice = async (paymentType) => {
    if (!pendingPayment) return;
    await submitPayment({
      invoice_id: pendingPayment.invoice_id,
      amount: pendingPayment.amount,
      method: pendingPayment.method,
      reference: pendingPayment.reference,
      payment_type: paymentType, // 'don' or 'regulier' (regulier means accept trop_percu)
    });
  };

  const handleCreateRegularization = async (e) => {
    e.preventDefault();
    const raw = parseFloat(regularizationFormData.amount);
    if (isNaN(raw) || raw <= 0) {
      toast.error('Le montant à régulariser doit être positif');
      return;
    }
    await submitPayment({
      invoice_id: selectedInvoice.id,
      amount: -Math.abs(raw),
      method: regularizationFormData.method,
      reference: regularizationFormData.reference,
      payment_type: regularizationFormData.payment_type,
    });
  };

  const handleQuoteStatusChange = async (quoteId, newStatus, purchaseOrderValue = null) => {
    try {
      let url = `/quotes/${quoteId}/status?status=${newStatus}`;
      if (purchaseOrderValue) {
        url += `&purchase_order=${encodeURIComponent(purchaseOrderValue)}`;
      }
      await api.put(url);
      toast.success(newStatus === 'accepte' ? 'Devis accepté, appel à participation / note de débit générée' : 'Statut mis à jour');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };

  // Handle quote acceptance with purchase order modal
  const handleAcceptQuote = (quote) => {
    // For non-particulier clients, show modal for purchase order
    if (quote.client_type && quote.client_type !== 'particulier') {
      setAcceptQuoteDialog({ open: true, quote });
      setPurchaseOrder('');
    } else {
      // Direct acceptance for particulier
      handleQuoteStatusChange(quote.id, 'accepte');
    }
  };

  const confirmAcceptQuote = async () => {
    if (acceptQuoteDialog.quote) {
      await handleQuoteStatusChange(acceptQuoteDialog.quote.id, 'accepte', purchaseOrder);
      setAcceptQuoteDialog({ open: false, quote: null });
      setPurchaseOrder('');
    }
  };

  // Open edit quote dialog
  const openEditQuoteDialog = (quote) => {
    setEditQuoteFormData({ amount: quote.amount.toString(), description: quote.description });
    setEditQuoteDialog({ open: true, quote });
  };

  // Update quote content (amount, description)
  const handleUpdateQuote = async () => {
    if (!editQuoteDialog.quote) return;
    
    try {
      await api.put(`/quotes/${editQuoteDialog.quote.id}`, {
        amount: parseFloat(editQuoteFormData.amount),
        description: editQuoteFormData.description
      });
      toast.success('Devis modifié avec succès');
      setEditQuoteDialog({ open: false, quote: null });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la modification');
    }
  };

  // Reset quote status (for admin to reopen a quote)
  const handleResetQuoteStatus = async (quoteId, newStatus) => {
    try {
      await api.put(`/quotes/${quoteId}/status?status=${newStatus}`);
      toast.success(`Statut du devis modifié en "${quoteStatusConfig[newStatus]?.label}"`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la modification');
    }
  };

  // Validate quote with Purchase Order (Bon de commande) - optional
  const handleValidateWithPO = async () => {
    if (!poValidationDialog.quote) {
      return;
    }
    
    setPoValidating(true);
    try {
      await api.post(`/quotes/${poValidationDialog.quote.id}/validate-with-po`, {
        purchase_order: poNumber.trim()
      });
      toast.success('Validation confirmée, appel à participation généré');
      setPoValidationDialog({ open: false, quote: null });
      setPoNumber('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la validation');
    } finally {
      setPoValidating(false);
    }
  };

  // Open PO validation dialog
  const openPoValidationDialog = (quote) => {
    setPoValidationDialog({ open: true, quote });
    setPoNumber('');
  };

  // Load reminders for an invoice
  const loadInvoiceReminders = async (invoice) => {
    setLoadingReminders(true);
    setRemindersDialog({ open: true, invoice });
    try {
      const response = await api.get(`/invoices/${invoice.id}/reminders`);
      setInvoiceReminders(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des relances');
      setInvoiceReminders([]);
    } finally {
      setLoadingReminders(false);
    }
  };

  // Send manual reminder
  const handleSendReminder = async () => {
    if (!remindersDialog.invoice) return;
    
    setSendingReminder(true);
    try {
      await api.post(`/invoices/${remindersDialog.invoice.id}/reminders/send`);
      toast.success('Relance envoyée avec succès');
      // Reload reminders
      const response = await api.get(`/invoices/${remindersDialog.invoice.id}/reminders`);
      setInvoiceReminders(response.data);
      loadData(); // Refresh invoices list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi de la relance');
    } finally {
      setSendingReminder(false);
    }
  };

  const handleInvoiceStatusChange = async (invoiceId, newStatus) => {
    try {
      await api.put(`/invoices/${invoiceId}/status?status=${newStatus}`);
      toast.success('Statut mis à jour');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };

  const handleSendQuote = async (quoteId) => {
    try {
      await api.post(`/quotes/${quoteId}/send`);
      toast.success('Devis envoyé par email');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    }
  };

  const handleSendInvoice = async (invoiceId) => {
    try {
      await api.post(`/invoices/${invoiceId}/send`);
      toast.success('Appel à participation envoyé par email');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    }
  };

  const handleDownloadQuotePdf = async (quote) => {
    try {
      const response = await api.get(`/quotes/${quote.id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Devis_${quote.quote_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const handleDownloadInvoicePdf = async (invoice) => {
    try {
      const response = await api.get(`/invoices/${invoice.id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `AppelParticipation_${invoice.invoice_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const handlePrintInvoice = async (invoice) => {
    try {
      const response = await api.get(`/invoices/${invoice.id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const printWindow = window.open(url, '_blank');
      printWindow.onload = () => {
        printWindow.print();
      };
    } catch (error) {
      toast.error('Erreur lors de l\'impression');
    }
  };

  const handleExportAccounting = async (format) => {
    try {
      let params = `period=${exportPeriod}&format=${format}`;
      if (exportPeriod === 'custom' && exportStartDate && exportEndDate) {
        params += `&start_date=${exportStartDate}&end_date=${exportEndDate}`;
      }
      
      const response = await api.get(`/accounting/export?${params}`, { responseType: 'blob' });
      const contentType = format === 'csv' ? 'text/csv' : 'application/pdf';
      const url = window.URL.createObjectURL(new Blob([response.data], { type: contentType }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Export_Comptable.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`Export ${format.toUpperCase()} téléchargé`);
      setExportDialogOpen(false);
    } catch (error) {
      toast.error('Erreur lors de l\'export');
    }
  };

  // Delete/Cancel handlers (Admin only)
  const handleDeleteQuote = async () => {
    try {
      await api.delete(`/quotes/${deleteQuoteDialog.quote.id}`);
      toast.success('Devis supprimé');
      setDeleteQuoteDialog({ open: false, quote: null });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const handleCancelQuote = async () => {
    try {
      await api.put(`/quotes/${cancelQuoteDialog.quote.id}/cancel`);
      toast.success('Devis annulé');
      setCancelQuoteDialog({ open: false, quote: null });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'annulation');
    }
  };

  const handleDeleteInvoice = async () => {
    try {
      await api.delete(`/invoices/${deleteInvoiceDialog.invoice.id}`);
      toast.success('Appel à participation supprimé');
      setDeleteInvoiceDialog({ open: false, invoice: null });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const handleCancelInvoice = async () => {
    try {
      await api.put(`/invoices/${cancelInvoiceDialog.invoice.id}/cancel`);
      toast.success('Appel à participation annulé');
      setCancelInvoiceDialog({ open: false, invoice: null });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'annulation');
    }
  };

  // Calculate totals
  const totalPending = invoices
    .filter(inv => inv.status === 'en_attente' || inv.status === 'partiel')
    .reduce((sum, inv) => sum + (inv.amount - (inv.amount_paid || 0)), 0);
  
  const totalPaid = invoices
    .filter(inv => inv.status === 'payee')
    .reduce((sum, inv) => sum + inv.amount, 0);

  // Filtered invoices
  const filteredInvoices = useMemo(() => {
    return invoices.filter(inv => {
      // Status filter
      if (invoiceStatusFilter !== 'all' && inv.status !== invoiceStatusFilter) {
        return false;
      }
      // Date filter - compare only date parts (year, month, day)
      if (inv.created_at && (invoiceStartDate || invoiceEndDate)) {
        const invoiceDate = new Date(inv.created_at);
        invoiceDate.setHours(0, 0, 0, 0);
        
        if (invoiceStartDate) {
          const startDate = new Date(invoiceStartDate);
          startDate.setHours(0, 0, 0, 0);
          if (invoiceDate < startDate) return false;
        }
        if (invoiceEndDate) {
          const endDate = new Date(invoiceEndDate);
          endDate.setHours(23, 59, 59, 999);
          if (invoiceDate > endDate) return false;
        }
      }
      return true;
    });
  }, [invoices, invoiceStatusFilter, invoiceStartDate, invoiceEndDate]);

  // Filtered quotes
  const filteredQuotes = useMemo(() => {
    return quotes.filter(quote => {
      // Status filter
      if (quoteStatusFilter !== 'all' && quote.status !== quoteStatusFilter) {
        return false;
      }
      // Date filter - compare only date parts (year, month, day)
      if (quote.created_at && (quoteStartDate || quoteEndDate)) {
        const quoteDate = new Date(quote.created_at);
        quoteDate.setHours(0, 0, 0, 0);
        
        if (quoteStartDate) {
          const startDate = new Date(quoteStartDate);
          startDate.setHours(0, 0, 0, 0);
          if (quoteDate < startDate) return false;
        }
        if (quoteEndDate) {
          const endDate = new Date(quoteEndDate);
          endDate.setHours(23, 59, 59, 999);
          if (quoteDate > endDate) return false;
        }
      }
      return true;
    });
  }, [quotes, quoteStatusFilter, quoteStartDate, quoteEndDate]);

  // Paginated invoices
  const paginatedInvoices = useMemo(() => {
    if (invoicePageSize === 'all') return filteredInvoices;
    const startIndex = (invoiceCurrentPage - 1) * invoicePageSize;
    return filteredInvoices.slice(startIndex, startIndex + invoicePageSize);
  }, [filteredInvoices, invoiceCurrentPage, invoicePageSize]);

  const invoiceTotalPages = useMemo(() => {
    if (invoicePageSize === 'all') return 1;
    return Math.ceil(filteredInvoices.length / invoicePageSize);
  }, [filteredInvoices.length, invoicePageSize]);

  // Paginated quotes
  const paginatedQuotes = useMemo(() => {
    if (quotePageSize === 'all') return filteredQuotes;
    const startIndex = (quoteCurrentPage - 1) * quotePageSize;
    return filteredQuotes.slice(startIndex, startIndex + quotePageSize);
  }, [filteredQuotes, quoteCurrentPage, quotePageSize]);

  const quoteTotalPages = useMemo(() => {
    if (quotePageSize === 'all') return 1;
    return Math.ceil(filteredQuotes.length / quotePageSize);
  }, [filteredQuotes.length, quotePageSize]);

  // Reset page when filters change
  useEffect(() => {
    setInvoiceCurrentPage(1);
  }, [invoiceStatusFilter, invoiceStartDate, invoiceEndDate, invoicePageSize]);

  useEffect(() => {
    setQuoteCurrentPage(1);
  }, [quoteStatusFilter, quoteStartDate, quoteEndDate, quotePageSize]);

  // Reset filters
  const resetInvoiceFilters = () => {
    setInvoiceStatusFilter('all');
    setInvoiceStartDate('');
    setInvoiceEndDate('');
  };

  const resetQuoteFilters = () => {
    setQuoteStatusFilter('all');
    setQuoteStartDate('');
    setQuoteEndDate('');
  };

  return (
    <Layout>
      <PageHeader 
        title="Contributions" 
        description={isUser ? "Vos devis et appels à participation" : "Gérez les devis, appels à participation et règlements"}
      />

      <div className="p-8 space-y-6" data-testid="billing-content">
        {/* Summary Cards - Hidden for regular users */}
        {!isUser && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <Card className="border-slate-200">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-500 uppercase">Devis en cours</p>
                    <p className="text-2xl font-bold text-[#1e3a8a]">{quotes.filter(q => q.status === 'envoye').length}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-blue-100">
                    <FileText className="text-blue-600" size={24} />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Card for quotes waiting for Purchase Order */}
            <Card className="border-slate-200 border-amber-300">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-amber-600 uppercase">En attente BC</p>
                    <p className="text-2xl font-bold text-amber-600">{quotes.filter(q => q.status === 'accepte_attente_bc').length}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-amber-100">
                    <ClipboardList className="text-amber-600" size={24} />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-slate-200">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-500 uppercase">En attente règlement</p>
                    <p className="text-2xl font-bold text-amber-600">{totalPending.toLocaleString('fr-FR')} €</p>
                  </div>
                  <div className="p-3 rounded-lg bg-amber-100">
                    <Euro className="text-amber-600" size={24} />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-slate-200">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-500 uppercase">Total perçu</p>
                    <p className="text-2xl font-bold text-emerald-600">{totalPaid.toLocaleString('fr-FR')} €</p>
                  </div>
                  <div className="p-3 rounded-lg bg-emerald-100">
                    <Receipt className="text-emerald-600" size={24} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="flex justify-between items-center mb-4">
            <TabsList>
              <TabsTrigger value="invoices" data-testid="tab-invoices">Appels à participation</TabsTrigger>
              <TabsTrigger value="quotes" data-testid="tab-quotes">Devis</TabsTrigger>
            </TabsList>
            
            {/* Action buttons - Hidden for regular users */}
            {!isUser && (
              <div className="flex gap-2">
                <Button
                  onClick={() => setExportDialogOpen(true)}
                  variant="outline"
                  className="border-[#1e3a8a] text-[#1e3a8a]"
                  data-testid="export-accounting-button"
                >
                  <FileSpreadsheet className="mr-2 h-4 w-4" />
                  Export comptable
                </Button>
                {activeTab === 'quotes' && (
                  <Button
                    onClick={() => setQuoteDialogOpen(true)}
                    className="bg-[#1e3a8a] hover:bg-[#1e40af] btn-animate"
                    data-testid="add-quote-button"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Nouveau devis
                  </Button>
                )}
                {activeTab === 'invoices' && (
                  <Button
                    onClick={() => setInvoiceDialogOpen(true)}
                    className="bg-[#1e3a8a] hover:bg-[#1e40af] btn-animate"
                    data-testid="add-invoice-button"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Nouvel appel
                  </Button>
                )}
              </div>
            )}
          </div>

          <TabsContent value="invoices">
            <InvoicesTab
              invoices={invoices} filteredInvoices={filteredInvoices} paginatedInvoices={paginatedInvoices} loading={loading}
              invoiceStatusFilter={invoiceStatusFilter} setInvoiceStatusFilter={setInvoiceStatusFilter}
              invoiceStartDate={invoiceStartDate} setInvoiceStartDate={setInvoiceStartDate}
              invoiceEndDate={invoiceEndDate} setInvoiceEndDate={setInvoiceEndDate}
              invoicePageSize={invoicePageSize} setInvoicePageSize={setInvoicePageSize}
              invoiceCurrentPage={invoiceCurrentPage} setInvoiceCurrentPage={setInvoiceCurrentPage} invoiceTotalPages={invoiceTotalPages}
              isUser={isUser} isAdmin={isAdmin}
              resetInvoiceFilters={resetInvoiceFilters}
              onDownloadPdf={handleDownloadInvoicePdf} onPrint={handlePrintInvoice} onSend={handleSendInvoice}
              onLoadReminders={loadInvoiceReminders}
              onRecordPayment={(invoice) => {
                setSelectedInvoice(invoice);
                setPaymentFormData({ amount: (invoice.amount - (invoice.amount_paid || 0)).toString(), method: 'virement', reference: '' });
                setPaymentDialogOpen(true);
              }}
              onRecordRegularization={(invoice) => {
                setSelectedInvoice(invoice);
                const excess = Math.max(0, Number(invoice.amount_paid || 0) - Number(invoice.amount));
                setRegularizationFormData({
                  amount: excess > 0 ? excess.toFixed(2) : '',
                  method: 'virement',
                  reference: '',
                  payment_type: 'regularisation_trop_percu',
                });
                setRegularizationDialogOpen(true);
              }}
              onStatusChange={handleInvoiceStatusChange}
              onCancel={(invoice) => setCancelInvoiceDialog({ open: true, invoice })}
              onDelete={(invoice) => setDeleteInvoiceDialog({ open: true, invoice })}
            />
          </TabsContent>

          <TabsContent value="quotes">
            <QuotesTab
              quotes={quotes} filteredQuotes={filteredQuotes} paginatedQuotes={paginatedQuotes} loading={loading}
              quoteStatusFilter={quoteStatusFilter} setQuoteStatusFilter={setQuoteStatusFilter}
              quoteStartDate={quoteStartDate} setQuoteStartDate={setQuoteStartDate}
              quoteEndDate={quoteEndDate} setQuoteEndDate={setQuoteEndDate}
              quotePageSize={quotePageSize} setQuotePageSize={setQuotePageSize}
              quoteCurrentPage={quoteCurrentPage} setQuoteCurrentPage={setQuoteCurrentPage} quoteTotalPages={quoteTotalPages}
              isUser={isUser} isAdmin={isAdmin} canManage={isAdmin || isGestionnaire}
              resetQuoteFilters={resetQuoteFilters}
              onDownloadPdf={handleDownloadQuotePdf} onAccept={handleAcceptQuote}
              onStatusChange={handleQuoteStatusChange} onResetStatus={handleResetQuoteStatus}
              onOpenPoValidation={openPoValidationDialog} onOpenEditQuote={openEditQuoteDialog}
              onSend={handleSendQuote}
              onCancel={(quote) => setCancelQuoteDialog({ open: true, quote })}
              onDelete={(quote) => setDeleteQuoteDialog({ open: true, quote })}
            />
          </TabsContent>
        </Tabs>
      </div>

      {/* Quote Dialog */}
      <Dialog open={quoteDialogOpen} onOpenChange={setQuoteDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#1e3a8a]" style={{ fontFamily: 'Merriweather, serif' }}>
              Nouveau devis
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateQuote} className="space-y-4">
            <div className="space-y-2">
              <Label>Réservation *</Label>
              <Select 
                value={quoteFormData.reservation_id} 
                onValueChange={(value) => setQuoteFormData({ ...quoteFormData, reservation_id: value })}
              >
                <SelectTrigger data-testid="quote-reservation-select">
                  <SelectValue placeholder="Sélectionner..." />
                </SelectTrigger>
                <SelectContent>
                  {reservations.map(res => (
                    <SelectItem key={res.id} value={res.id}>
                      {res.title} - {res.client_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Montant (€) *</Label>
              <Input
                type="number"
                value={quoteFormData.amount}
                onChange={(e) => setQuoteFormData({ ...quoteFormData, amount: e.target.value })}
                required
                min="0"
                step="0.01"
                placeholder="150.00"
                data-testid="quote-amount-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={quoteFormData.description}
                onChange={(e) => setQuoteFormData({ ...quoteFormData, description: e.target.value })}
                placeholder="Détails du devis..."
                rows={3}
                data-testid="quote-description-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setQuoteDialogOpen(false)}>
                Annuler
              </Button>
              <Button type="submit" className="bg-[#1e3a8a] hover:bg-[#1e40af]" data-testid="save-quote-button">
                Créer
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Invoice Dialog */}
      <Dialog open={invoiceDialogOpen} onOpenChange={setInvoiceDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#1e3a8a]" style={{ fontFamily: 'Merriweather, serif' }}>
              Nouvel appel à participation
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateInvoice} className="space-y-4">
            <div className="space-y-2">
              <Label>Mise à disposition *</Label>
              <Select 
                value={invoiceFormData.reservation_id} 
                onValueChange={(value) => setInvoiceFormData({ ...invoiceFormData, reservation_id: value })}
              >
                <SelectTrigger data-testid="invoice-reservation-select">
                  <SelectValue placeholder="Sélectionner..." />
                </SelectTrigger>
                <SelectContent>
                  {reservations.map(res => (
                    <SelectItem key={res.id} value={res.id}>
                      {res.title} - {res.client_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Participation aux frais (€) *</Label>
              <Input
                type="number"
                value={invoiceFormData.amount}
                onChange={(e) => setInvoiceFormData({ ...invoiceFormData, amount: e.target.value })}
                required
                min="0"
                step="0.01"
                placeholder="150.00"
                data-testid="invoice-amount-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={invoiceFormData.description}
                onChange={(e) => setInvoiceFormData({ ...invoiceFormData, description: e.target.value })}
                placeholder="Mise à disposition de salle..."
                rows={3}
                data-testid="invoice-description-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setInvoiceDialogOpen(false)}>
                Annuler
              </Button>
              <Button type="submit" className="bg-[#1e3a8a] hover:bg-[#1e40af]" data-testid="save-invoice-button">
                Créer
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#1e3a8a]" style={{ fontFamily: 'Merriweather, serif' }}>
              Enregistrer un règlement
            </DialogTitle>
          </DialogHeader>
          {selectedInvoice && (
            <div className="mb-4 p-3 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-600">
                Appel à participation : <strong>{selectedInvoice.invoice_number}</strong>
              </p>
              <p className="text-sm text-slate-600">
                Reste à verser : <strong>{(selectedInvoice.amount - (selectedInvoice.amount_paid || 0)).toLocaleString('fr-FR')} €</strong>
              </p>
            </div>
          )}
          <form onSubmit={handleCreatePayment} className="space-y-4">
            <div className="space-y-2">
              <Label>Montant (€) *</Label>
              <Input
                type="number"
                value={paymentFormData.amount}
                onChange={(e) => setPaymentFormData({ ...paymentFormData, amount: e.target.value })}
                required
                min="0"
                step="0.01"
                data-testid="payment-amount-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Mode de paiement *</Label>
              <Select 
                value={paymentFormData.method} 
                onValueChange={(value) => setPaymentFormData({ ...paymentFormData, method: value })}
              >
                <SelectTrigger data-testid="payment-method-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="especes">Espèces</SelectItem>
                  <SelectItem value="cheque">Chèque</SelectItem>
                  <SelectItem value="virement">Virement</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Référence</Label>
              <Input
                value={paymentFormData.reference}
                onChange={(e) => setPaymentFormData({ ...paymentFormData, reference: e.target.value })}
                placeholder="N° chèque, référence virement..."
                data-testid="payment-reference-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setPaymentDialogOpen(false)}>
                Annuler
              </Button>
              <Button type="submit" className="bg-[#1e3a8a] hover:bg-[#1e40af]" data-testid="save-payment-button">
                Enregistrer
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Overpayment Choice Dialog: Don vs Trop-perçu */}
      <Dialog open={overpayDialogOpen} onOpenChange={setOverpayDialogOpen}>
        <DialogContent className="max-w-md" data-testid="overpay-choice-dialog">
          <DialogHeader>
            <DialogTitle className="text-orange-700" style={{ fontFamily: 'Merriweather, serif' }}>
              Montant supérieur au reste à verser
            </DialogTitle>
            <DialogDescription>
              Le règlement saisi dépasse de <strong>{pendingPayment?.excess?.toLocaleString('fr-FR')} €</strong> le reste dû. Choisissez la nature de cet excédent :
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <button
              type="button"
              onClick={() => handleConfirmOverpayChoice('don')}
              className="w-full text-left border border-emerald-200 rounded-lg p-3 hover:bg-emerald-50 transition-colors"
              data-testid="overpay-choice-don"
            >
              <p className="font-semibold text-emerald-800">Don</p>
              <p className="text-xs text-slate-600 mt-1">L'excédent est conservé comme don. L'appel passe au statut « Réglé ».</p>
            </button>
            <button
              type="button"
              onClick={() => handleConfirmOverpayChoice('trop_percu')}
              className="w-full text-left border border-orange-200 rounded-lg p-3 hover:bg-orange-50 transition-colors"
              data-testid="overpay-choice-trop-percu"
            >
              <p className="font-semibold text-orange-800">Trop-perçu</p>
              <p className="text-xs text-slate-600 mt-1">L'appel passe au statut « Trop-perçu » et devra être régularisé ultérieurement.</p>
            </button>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => { setOverpayDialogOpen(false); setPendingPayment(null); }}>
              Annuler
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Regularization Dialog: negative payment with type */}
      <Dialog open={regularizationDialogOpen} onOpenChange={setRegularizationDialogOpen}>
        <DialogContent className="max-w-md" data-testid="regularization-dialog">
          <DialogHeader>
            <DialogTitle className="text-orange-700" style={{ fontFamily: 'Merriweather, serif' }}>
              Saisir une régularisation
            </DialogTitle>
          </DialogHeader>
          {selectedInvoice && (
            <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
              <p className="text-sm text-slate-700">
                Appel à participation : <strong>{selectedInvoice.invoice_number}</strong>
              </p>
              <p className="text-sm text-slate-700">
                Total appelé : <strong>{Number(selectedInvoice.amount).toLocaleString('fr-FR')} €</strong>
              </p>
              <p className="text-sm text-slate-700">
                Total versé : <strong>{Number(selectedInvoice.amount_paid || 0).toLocaleString('fr-FR')} €</strong>
              </p>
              {Number(selectedInvoice.amount_paid || 0) > Number(selectedInvoice.amount) && (
                <p className="text-sm text-orange-700 font-semibold mt-1">
                  Trop-perçu : {(Number(selectedInvoice.amount_paid) - Number(selectedInvoice.amount)).toLocaleString('fr-FR')} €
                </p>
              )}
            </div>
          )}
          <form onSubmit={handleCreateRegularization} className="space-y-4">
            <div className="space-y-2">
              <Label>Type de régularisation *</Label>
              <Select
                value={regularizationFormData.payment_type}
                onValueChange={(value) => setRegularizationFormData({ ...regularizationFormData, payment_type: value })}
              >
                <SelectTrigger data-testid="regularization-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="regularisation_trop_percu">Régularisation d'un trop-perçu</SelectItem>
                  <SelectItem value="erreur_saisie">Erreur de saisie</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">
                {regularizationFormData.payment_type === 'regularisation_trop_percu'
                  ? "Le bénéficiaire est remboursé : sortie d'argent enregistrée."
                  : "Annulation d'un règlement saisi par erreur (aucun mouvement de fonds réel)."}
              </p>
            </div>
            <div className="space-y-2">
              <Label>Montant à régulariser (€) *</Label>
              <Input
                type="number"
                value={regularizationFormData.amount}
                onChange={(e) => setRegularizationFormData({ ...regularizationFormData, amount: e.target.value })}
                required
                min="0.01"
                step="0.01"
                placeholder="ex: 25.00"
                data-testid="regularization-amount-input"
              />
              <p className="text-xs text-slate-500">Saisissez un montant positif. Il sera décompté du total versé.</p>
            </div>
            <div className="space-y-2">
              <Label>Mode *</Label>
              <Select
                value={regularizationFormData.method}
                onValueChange={(value) => setRegularizationFormData({ ...regularizationFormData, method: value })}
              >
                <SelectTrigger data-testid="regularization-method-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="especes">Espèces</SelectItem>
                  <SelectItem value="cheque">Chèque</SelectItem>
                  <SelectItem value="virement">Virement</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Référence</Label>
              <Input
                value={regularizationFormData.reference}
                onChange={(e) => setRegularizationFormData({ ...regularizationFormData, reference: e.target.value })}
                placeholder="N° chèque de remboursement, motif..."
                data-testid="regularization-reference-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setRegularizationDialogOpen(false)}>
                Annuler
              </Button>
              <Button
                type="submit"
                className="bg-orange-600 hover:bg-orange-700 text-white"
                data-testid="save-regularization-button"
              >
                Enregistrer la régularisation
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Export Accounting Dialog */}
      <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#1e3a8a]" style={{ fontFamily: 'Merriweather, serif' }}>
              Export comptable
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Période</Label>
              <Select value={exportPeriod} onValueChange={setExportPeriod}>
                <SelectTrigger data-testid="export-period-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="current_month">Mois en cours</SelectItem>
                  <SelectItem value="current_year">Année en cours</SelectItem>
                  <SelectItem value="previous_year">Année précédente</SelectItem>
                  <SelectItem value="custom">Période personnalisée</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {exportPeriod === 'custom' && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date début</Label>
                  <Input
                    type="date"
                    value={exportStartDate}
                    onChange={(e) => setExportStartDate(e.target.value)}
                    data-testid="export-start-date"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Date fin</Label>
                  <Input
                    type="date"
                    value={exportEndDate}
                    onChange={(e) => setExportEndDate(e.target.value)}
                    data-testid="export-end-date"
                  />
                </div>
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <Button
                onClick={() => handleExportAccounting('csv')}
                variant="outline"
                className="flex-1"
                data-testid="export-csv-button"
              >
                <FileSpreadsheet className="mr-2 h-4 w-4" />
                Télécharger CSV
              </Button>
              <Button
                onClick={() => handleExportAccounting('pdf')}
                className="flex-1 bg-[#1e3a8a] hover:bg-[#1e40af]"
                data-testid="export-pdf-button"
              >
                <Download className="mr-2 h-4 w-4" />
                Télécharger PDF
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Quote Confirmation */}
      <AlertDialog open={deleteQuoteDialog.open} onOpenChange={(open) => setDeleteQuoteDialog({ ...deleteQuoteDialog, open })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer le devis ?</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer le devis {deleteQuoteDialog.quote?.quote_number} ?
              Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteQuote} className="bg-red-600 hover:bg-red-700">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cancel Quote Confirmation */}
      <AlertDialog open={cancelQuoteDialog.open} onOpenChange={(open) => setCancelQuoteDialog({ ...cancelQuoteDialog, open })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Annuler le devis ?</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir annuler le devis {cancelQuoteDialog.quote?.quote_number} ?
              La réservation associée sera également annulée.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Non, garder</AlertDialogCancel>
            <AlertDialogAction onClick={handleCancelQuote} className="bg-amber-600 hover:bg-amber-700">
              Oui, annuler
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Invoice Confirmation */}
      <AlertDialog open={deleteInvoiceDialog.open} onOpenChange={(open) => setDeleteInvoiceDialog({ ...deleteInvoiceDialog, open })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer l'appel à participation ?</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer l'appel {deleteInvoiceDialog.invoice?.invoice_number} ?
              Tous les règlements associés seront également supprimés. Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteInvoice} className="bg-red-600 hover:bg-red-700">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cancel Invoice Confirmation */}
      <AlertDialog open={cancelInvoiceDialog.open} onOpenChange={(open) => setCancelInvoiceDialog({ ...cancelInvoiceDialog, open })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Annuler l'appel à participation ?</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir annuler l'appel {cancelInvoiceDialog.invoice?.invoice_number} ?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Non, garder</AlertDialogCancel>
            <AlertDialogAction onClick={handleCancelInvoice} className="bg-amber-600 hover:bg-amber-700">
              Oui, annuler
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Accept Quote with Purchase Order */}
      <Dialog open={acceptQuoteDialog.open} onOpenChange={(open) => { setAcceptQuoteDialog({ ...acceptQuoteDialog, open }); setPurchaseOrder(''); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Valider le devis</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-gray-600">
              Vous êtes sur le point d'accepter le devis <strong>{acceptQuoteDialog.quote?.quote_number}</strong>.
              Une note de débit sera automatiquement générée.
            </p>
            <div className="space-y-2">
              <Label htmlFor="purchase_order">
                Numéro de bon de commande <span className="text-slate-400">(facultatif)</span>
              </Label>
              <Input
                id="purchase_order"
                value={purchaseOrder}
                onChange={(e) => setPurchaseOrder(e.target.value)}
                placeholder="Ex: BC-2025-001234"
                data-testid="purchase-order-input"
              />
              <p className="text-xs text-slate-500">Ce numéro apparaîtra sur la note de débit</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAcceptQuoteDialog({ open: false, quote: null })}>
              Annuler
            </Button>
            <Button onClick={confirmAcceptQuote} className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-accept-quote">
              <Check className="h-4 w-4 mr-2" />
              Valider le devis
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Quote Dialog */}
      <Dialog open={editQuoteDialog.open} onOpenChange={(open) => setEditQuoteDialog({ ...editQuoteDialog, open })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Modifier le devis</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-gray-600 mb-4">
              Modification du devis <strong>{editQuoteDialog.quote?.quote_number}</strong>
            </p>
            <div className="space-y-2">
              <Label htmlFor="edit_amount">Montant (€) *</Label>
              <Input
                id="edit_amount"
                type="number"
                step="0.01"
                min="0"
                value={editQuoteFormData.amount}
                onChange={(e) => setEditQuoteFormData({ ...editQuoteFormData, amount: e.target.value })}
                placeholder="100.00"
                data-testid="edit-quote-amount"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit_description">Description *</Label>
              <Input
                id="edit_description"
                value={editQuoteFormData.description}
                onChange={(e) => setEditQuoteFormData({ ...editQuoteFormData, description: e.target.value })}
                placeholder="Mise à disposition de salle..."
                data-testid="edit-quote-description"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditQuoteDialog({ open: false, quote: null })}>
              Annuler
            </Button>
            <Button onClick={handleUpdateQuote} className="bg-[#1e3a8a] hover:bg-[#1e40af]" data-testid="save-edit-quote">
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Purchase Order Validation Dialog */}
      <Dialog open={poValidationDialog.open} onOpenChange={(open) => setPoValidationDialog({ ...poValidationDialog, open })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#1e3a8a]" style={{ fontFamily: 'Merriweather, serif' }}>
              <ClipboardList className="inline-block mr-2 h-5 w-5" />
              Saisir le Bon de Commande
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm text-amber-800">
                <AlertTriangle className="inline-block mr-1 h-4 w-4" />
                Le devis <strong>{poValidationDialog.quote?.quote_number}</strong> a été accepté par le bénéficiaire.
              </p>
              <p className="text-sm text-amber-700 mt-2">
                Saisissez le numéro de bon de commande (facultatif) pour générer l'appel à participation / note de débit.
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="po_number">Numéro de bon de commande <span className="text-slate-400 text-xs">(facultatif)</span></Label>
              <Input
                id="po_number"
                value={poNumber}
                onChange={(e) => setPoNumber(e.target.value)}
                placeholder="Ex: BC-2025-001234"
                data-testid="po-number-input"
                autoFocus
              />
              <p className="text-xs text-slate-500">Vous pourrez l'ajouter plus tard si le bénéficiaire ne l'a pas encore fourni.</p>
            </div>
            
            <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-600">
              <p><strong>Bénéficiaire :</strong> {poValidationDialog.quote?.client_name}</p>
              <p><strong>Montant :</strong> {poValidationDialog.quote?.amount?.toLocaleString('fr-FR')} €</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPoValidationDialog({ open: false, quote: null })} disabled={poValidating}>
              Annuler
            </Button>
            <Button 
              onClick={handleValidateWithPO} 
              className="bg-emerald-600 hover:bg-emerald-700" 
              data-testid="validate-po-button"
              disabled={poValidating}
            >
              {poValidating ? (
                <>
                  <div className="spinner h-4 w-4 mr-2" />
                  Validation...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Valider et générer
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invoice Reminders Dialog */}
      <Dialog open={remindersDialog.open} onOpenChange={(open) => setRemindersDialog({ ...remindersDialog, open })}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#1e3a8a]" style={{ fontFamily: 'Merriweather, serif' }}>
              <Bell className="inline-block mr-2 h-5 w-5" />
              Relances - {remindersDialog.invoice?.invoice_number}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Invoice info */}
            <div className="bg-slate-50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Bénéficiaire :</span>
                <span className="font-medium">{remindersDialog.invoice?.client_name}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Montant total :</span>
                <span className="font-medium">{remindersDialog.invoice?.amount?.toLocaleString('fr-FR')} €</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Reste à régler :</span>
                <span className="font-medium text-red-600">
                  {((remindersDialog.invoice?.amount || 0) - (remindersDialog.invoice?.amount_paid || 0)).toLocaleString('fr-FR')} €
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Échéance :</span>
                <span className="font-medium">
                  {remindersDialog.invoice?.due_date ? format(parseISO(remindersDialog.invoice.due_date), 'd MMM yyyy', { locale: fr }) : '-'}
                </span>
              </div>
            </div>

            {/* Reminders list */}
            <div>
              <h4 className="text-sm font-medium text-slate-700 mb-2 flex items-center">
                <Clock className="h-4 w-4 mr-1" />
                Historique des relances ({invoiceReminders.length})
              </h4>
              
              {loadingReminders ? (
                <div className="flex justify-center py-4">
                  <div className="spinner" style={{ width: 24, height: 24 }} />
                </div>
              ) : invoiceReminders.length === 0 ? (
                <div className="text-center py-4 text-slate-500 text-sm bg-slate-50 rounded-lg">
                  <Mail className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                  Aucune relance envoyée
                </div>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {invoiceReminders.map((reminder, index) => (
                    <div key={reminder.id || index} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-full ${
                          reminder.reminder_type === 'j21' ? 'bg-red-100 text-red-600' :
                          reminder.reminder_type === 'j14' ? 'bg-orange-100 text-orange-600' :
                          reminder.reminder_type === 'j7' ? 'bg-amber-100 text-amber-600' :
                          'bg-blue-100 text-blue-600'
                        }`}>
                          <Bell className="h-4 w-4" />
                        </div>
                        <div>
                          <p className="text-sm font-medium">
                            {reminder.reminder_type === 'manual' ? 'Relance manuelle' :
                             reminder.reminder_type === 'j7' ? 'Relance J+7' :
                             reminder.reminder_type === 'j14' ? 'Relance J+14' :
                             reminder.reminder_type === 'j21' ? 'Relance J+21 (dernière)' :
                             reminder.reminder_type}
                          </p>
                          <p className="text-xs text-slate-500">
                            {reminder.sent_at ? format(parseISO(reminder.sent_at), "d MMM yyyy 'à' HH:mm", { locale: fr }) : '-'}
                            {reminder.sent_by_name && ` • Par ${reminder.sent_by_name}`}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setRemindersDialog({ open: false, invoice: null })}>
              Fermer
            </Button>
            <Button 
              onClick={handleSendReminder}
              className="bg-amber-600 hover:bg-amber-700"
              disabled={sendingReminder}
              data-testid="send-reminder-button"
            >
              {sendingReminder ? (
                <>
                  <div className="spinner h-4 w-4 mr-2" />
                  Envoi...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Envoyer une relance
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
